import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import PackageRequest from './PackageRequest';

const { FiCheck, FiClock, FiCalendar, FiStar } = FiIcons;

const PricingSection = () => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <section id="pricing" className="py-20 bg-white">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6 }} 
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Wie wir zusammenarbeiten
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Der erste Schritt ist ein kurzes, kostenfreies Erstgespräch.<br />
            Wir schauen gemeinsam, wo du gerade stehst, was dich am meisten entlasten würde<br />
            und wie wir deine Buchhaltung sinnvoll aufbauen können.
          </p>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Du bekommst eine klare Einschätzung:<br />
            • Was in deinem Fall wirklich wichtig ist<br />
            • Welche Schritte nötig sind<br />
            • Welche Lösung für dich passt<br /><br />
            Wenn du danach weitermachen möchtest, kannst du direkt mit dem Quick Win Workshop starten.<br />
            Dort richten wir die wichtigsten Grundlagen ein, damit dein System läuft und du sofort Entlastung spürst.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <motion.a 
              href="https://nextcloud.managershop.com/index.php/apps/appointments/pub/Gb05r41McyYVyGQE/form" 
              target="_blank" 
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              className="bg-[#E30613] hover:bg-[#C10511] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 text-center shadow-lg"
            >
              Lass uns sprechen
            </motion.a>
            <motion.a 
              href="https://nextcloud.managershop.com/index.php/apps/appointments/pub/2cAlBQQqGVMD7tac/form" 
              target="_blank" 
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              className="bg-[#F3F1EE] hover:bg-[#e0dcd8] text-[#1A1A1A] px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 text-center shadow-lg border border-[#1A1A1A]"
            >
              Quick Win Workshop buchen
            </motion.a>
          </div>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
            Dein Weg in eine klare und entspannte Buchhaltung<br /><br />
            Nach unserem Gespräch wählen wir gemeinsam den für dich passenden nächsten Schritt.
          </p>
        </motion.div>

        {/* Package Tiles */}
        <div className="space-y-8 mb-16 max-w-4xl mx-auto">
          {/* Quick Win Workshop Tile */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }} 
            viewport={{ once: true }}
            className="bg-[#F3F1EE] rounded-xl p-8 shadow-lg"
          >
            <div className="flex items-start mb-6">
              <div className="bg-[#E30613] text-white w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <SafeIcon icon={FiClock} className="text-xl" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-[#1A1A1A] mb-2">Quick-Win-Workshop</h3>
                <p className="text-lg font-semibold text-[#1A1A1A]">Gemeinsam in die Umsetzung kommen</p>
              </div>
            </div>
            <p className="text-[#1A1A1A] mb-6">
              In 2-3 Stunden richten wir die wichtigsten Grundlagen für deine digitale Buchhaltung ein. Du bekommst Struktur, Klarheit und direkte Entlastung.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="text-[#1A1A1A]">
                <p><strong>Dauer:</strong> ca. 2-3 Stunden</p>
                <p><strong>Format:</strong> Online</p>
              </div>
              <div className="text-[#1A1A1A]">
                <p><strong>Angebotspreis:</strong> 249 € (zzgl. MwSt.)</p>
              </div>
            </div>
            <motion.a 
              href="https://nextcloud.managershop.com/index.php/apps/appointments/pub/2cAlBQQqGVMD7tac/form" 
              target="_blank" 
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              className="bg-[#E30613] hover:bg-[#C10511] text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 inline-block"
            >
              Quick Win Workshop buchen
            </motion.a>
          </motion.div>

          {/* Smarter Start Tile */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }} 
            viewport={{ once: true }}
            className="bg-[#F3F1EE] rounded-xl p-8 shadow-lg"
          >
            <div className="flex items-start mb-6">
              <div className="bg-[#E30613] text-white w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <SafeIcon icon={FiStar} className="text-xl" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-[#1A1A1A] mb-2">Smarter Start</h3>
                <p className="text-lg font-semibold text-[#1A1A1A]">– Dein Weg zur digitalen Routine</p>
              </div>
            </div>
            <p className="text-[#1A1A1A] mb-6">
              Schritt für Schritt zu einer stabilen Buchhaltungsroutine
            </p>
            <p className="text-[#1A1A1A] mb-6">
              Dieses Programm ist für dich, wenn du nach dem Quick Win dauerhaft sicher, klar strukturiert und ohne Stress arbeiten möchtest. Wir stabilisieren deine Abläufe, klären Fragen im Alltag und verankern Routinen, die funktionieren – nicht nur theoretisch, sondern in deiner realen Arbeitswoche.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="text-[#1A1A1A]">
                <p><strong>Dauer:</strong> ca. 4 Wochen</p>
                <p><strong>Format:</strong> Online mit begleitender Betreuung</p>
              </div>
              <div className="text-[#1A1A1A]">
                <p><strong>Angebotspreis:</strong> 1.500 € (zzgl. MwSt.)</p>
              </div>
            </div>
            <motion.a 
              href="https://nextcloud.managershop.com/index.php/apps/appointments/pub/Gb05r41McyYVyGQE/form" 
              target="_blank" 
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              className="bg-[#E30613] hover:bg-[#C10511] text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 inline-block"
            >
              Lass uns sprechen
            </motion.a>
          </motion.div>

          {/* Rundum Smart Tile */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }} 
            viewport={{ once: true }}
            className="bg-[#F3F1EE] rounded-xl p-8 shadow-lg"
          >
            <div className="flex items-start mb-6">
              <div className="bg-[#E30613] text-white w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <SafeIcon icon={FiCheck} className="text-xl" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-[#1A1A1A] mb-2">Rundum Smart</h3>
                <p className="text-lg font-semibold text-[#1A1A1A]">– Begleitende Unterstützung & Optimierung</p>
              </div>
            </div>
            <p className="text-[#1A1A1A] mb-6">
              Individuelle Begleitung für besondere Anforderungen
            </p>
            <p className="text-[#1A1A1A] mb-6">
              Wenn dein Geschäftsmodell komplexer ist oder du fortlaufende Unterstützung möchtest, begleite ich dich individuell. Wir passen Strukturen an, optimieren Workflows und entwickeln deine Buchhaltung mit deinem Unternehmen weiter.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="text-[#1A1A1A]">
                <p><strong>Preis:</strong> nach Absprache – je nach Umfang und Bedarf</p>
                <p><strong>Format:</strong> Online und bei Bedarf auch persönlich</p>
              </div>
            </div>
            <motion.a 
              href="https://nextcloud.managershop.com/index.php/apps/appointments/pub/Gb05r41McyYVyGQE/form" 
              target="_blank" 
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              className="bg-[#E30613] hover:bg-[#C10511] text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 inline-block"
            >
              Lass uns sprechen
            </motion.a>
          </motion.div>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6 }} 
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto"
        >
          <p className="text-xl text-gray-600">
            Du gehst mit dem ersten Gespräch keine Verpflichtung ein. Dieses Gespräch dient dazu, Klarheit zu gewinnen. Du entscheidest danach in Ruhe, ob und wie du weitermachen möchtest.
          </p>
        </motion.div>
      </div>
      <PackageRequest 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        packageName={selectedPackage} 
      />
    </section>
  );
};

export default PricingSection;